import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { authHeader } from '../../../services/authHeader';
import { setUser } from '../../../store/userSlice';
import BackButton from '../../Shared/BackButton';
import FormFeedback from '../../Shared/FormFeedback';
import styles from './ChangeSettingForm.module.css';

/**
 * Generic single-field account change form.
 *
 * Props:
 *   label        – field label text, also used in title ("Change {label}")
 *   endpoint     – PATCH URL (e.g. '/api/c/changeusername/')
 *   fieldType    – input type: 'text' | 'email'  (default 'text')
 *   apiParamName – request body key for the new value (e.g. 'new_username')
 *   reduxField   – key to update in the user Redux slice (e.g. 'username')
 *   required     – whether the input is required (default true)
 *   hint         – optional hint text shown below the input
 */
const ChangeSettingForm = ({
    label,
    endpoint,
    fieldType = 'text',
    apiParamName,
    reduxField,
    required = true,
    hint,
    validator,
    validationMessage,
    transform,
}) => {
    const [value, setValue] = useState('');
    const [touched, setTouched] = useState(false);
    const [error, setError] = useState(null);

    const isInvalid = validator && touched && value.length > 0 && !validator(value);
    const [successMessage, setSuccessMessage] = useState(null);
    const user = useSelector((state) => state.user);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);

        if (validator && !validator(value)) {
            setError(validationMessage || 'Invalid value.');
            return;
        }

        const submittedValue = transform ? transform(value) : value;
        try {
            await axios.patch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                [apiParamName]: submittedValue,
            }, authHeader());

            setSuccessMessage(`Successfully updated ${label.toLowerCase()}!`);
            setValue('');

            dispatch(setUser({ ...user, [reduxField]: submittedValue }));

            navigate(-1);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'An unexpected error occurred.';
            setError(errorMessage);
        }
    };

    return (
        <div className={styles.container}>
            <BackButton />
            <h1 className={styles.title}>Change {label}</h1>
            <form className={styles.form} onSubmit={handleSubmit}>
                <label className={styles.label}>
                    New {label}
                    <input
                        className={styles.input}
                        type={fieldType}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        onBlur={() => setTouched(true)}
                        placeholder={`Enter new ${label.toLowerCase()}`}
                        required={required || undefined}
                    />
                    {isInvalid && (
                        <span className={styles.matchError}>{validationMessage}</span>
                    )}
                </label>
                {hint && <p className={styles.hint}>{hint}</p>}
                <button className={styles.submitButton} type="submit">Update {label}</button>
            </form>
            <FormFeedback error={error} success={successMessage} />
        </div>
    );
};

export default ChangeSettingForm;
